import React, { useState } from 'react';
import {
  Box, Button, Grid, IconButton, Typography, Stack, Divider,
} from '@mui/material';
import { FieldArray, Formik } from 'formik';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@gabmarini/ckeditor5-build-classic-base64upload';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import { observer } from 'mobx-react';
import WushInputV2 from './WushInputV2';
import WushModal from '../../../components/WushModal';
import EventForm from './EventForm';
import InlineEvent from './InlineEvent';
import WushButton from '../../../components/WushButton';

const formikError = (formik, index, field) => {
  if (!formik.errors) return null;
  return (
    formik.touched?.costMatrix
    && formik.errors.costMatrix
    && formik.errors.costMatrix[index]?.[field]
  );
};

const EventSeriesForm = ({
  formik, eventCategoryStore, showEvents, update, eventSeriesCreating,
}) => {
  eventCategoryStore.getEventCategories();
  const [showCreateModal, setShowEventModal] = useState(false);
  const [event, setEvent] = useState(null);

  return (
    <Box>
      <Grid container>
        <Grid item md={12}>
          <Box>
            <Typography variant="h6">Basic Information</Typography>
          </Box>
        </Grid>
        <WushInputV2
          label="Name of Series"
          formik={formik}
          name="name"
          gridCol={3}
          helpText="The name of the series (ex. Fall Bike Series)"
        />
        <Box my={3}>
          <CKEditor
            editor={ClassicEditor}
            data={formik.values.description}
            onChange={(_e, editor) => {
              formik.setFieldValue('description', editor.getData());
            }}
          />
        </Box>
        <WushInputV2
          label="Additional Information"
          formik={formik}
          name="additionalInformation"
          inputType="textarea"
          gridCol={12}
          helpText="More information about the series"
        />
        <WushInputV2
          label="Start Date"
          formik={formik}
          name="startDatetime"
          inputType="date"
          gridCol={3}
          helpText="Date that the series starts"
        />
        <WushInputV2
          label="End Date"
          formik={formik}
          name="endDatetime"
          inputType="date"
          gridCol={3}
          helpText="Date that the series is over"
        />
        <WushInputV2
          label="Open to Registration"
          formik={formik}
          name="visibleDate"
          inputType="date"
          gridCol={3}
          helpText="Date that customers will be able to enroll for the series"
        />
        <Grid item md={3} />
        <Grid item md={12}>
          <Box mb={2} mt={2}>
            <Divider />
          </Box>
        </Grid>
        <Grid item md={12}>
          <Box>
            <Typography variant="h6">Event Settings</Typography>
          </Box>
        </Grid>
        <Grid item md={12} />
        <WushInputV2
          label="Enabled"
          name="enabled"
          inputType="checkbox"
          helpText="Check if this series should show to customers"
          gridCol={1}
        />
        <WushInputV2
          label="Dropins"
          name="allowDropins"
          inputType="checkbox"
          gridCol={1}
          helpText="Checking this will allow customers to enroll in events in the series without enrolling for the entire series"
        />
        <WushInputV2
          label="Waitlist"
          name="allowWaitlist"
          inputType="checkbox"
          gridCol={1}
          helpText="Checking this will allow customers to join a waitlist for specific events if the event is full"
        />
        <WushInputV2
          label="Restricted Pre-enrollment"
          name="restrictedPreEnrollment"
          inputType="checkbox"
          gridCol={3}
          helpText="Checking this will require customers to purchase the base price package for the event series until the event series starts. Customers will also only be able to select time slots instead of individual events (ex. Mondays at 8am or Fridays at 5am. These timeslots will be auto-generated based on the events added to this series."
        />
        <Grid md={12}>
          <Box mb={3} />
        </Grid>
        <WushInputV2
          label="Series Total Capacity"
          formik={formik}
          name="capacity"
          inputType="number"
          gridCol={3}
          helpText="The capacity of the events in the series"
        />
        <WushInputV2
          label="Category (optional)"
          formik={formik}
          name="eventCategoryId"
          inputType="autoselect"
          options={eventCategoryStore.eventCategories}
          gridCol={3}
          mb={2}
          helpText="The event category"
        />
        <Grid item md={6} />
        <Grid item md={12}>
          <Box mb={4} mt={2}>
            <Divider />
          </Box>
        </Grid>

        <Grid item md={12}>
          <Box>
            <Typography variant="h6">Series Pricing</Typography>
          </Box>
        </Grid>
        <Grid item md={12}>
          <Box>
            <Typography variant="body1">
              <strong>Series Base Price</strong>
              {' '}
              (This is the price for the full series)
            </Typography>
          </Box>
        </Grid>
        <WushInputV2
          label="Event count"
          formik={formik}
          inputType="number"
          name="eventCount"
          gridCol={3}
          helpText="Number of events a customer gets when they purchase the entire series"
        />
        <WushInputV2
          label="Cost"
          formik={formik}
          inputType="money"
          name="cost"
          gridCol={3}
          helpText="Cost of the series when the customer purchases the entire series"
        />
        <Grid item md={6} />
        <Grid item md={12}>
          <Box mt={3}>
            <Typography variant="body1">
              <strong>Additional Packages</strong>
              {' '}
              (optional)
            </Typography>
          </Box>
        </Grid>
        <FieldArray
          name="costMatrix"
          render={(arrayHelpers) => (
            <>
              {formik.values.costMatrix?.length === 0 && (
                <Button
                  variant="outlined"
                  onClick={
                    () => arrayHelpers.push({ eventCount: null, cost: null })
                  }
                >
                  Add cost package
                </Button>
              )}
              {formik.values.costMatrix?.map((_costRow, index) => (
                <>
                  <WushInputV2
                    label="Event count"
                    value={formik.values.costMatrix[index].eventCount}
                    onChange={(e) => formik.setFieldValue(`costMatrix.${index}.eventCount`, e.target.value)}
                    error={formikError(formik, index, 'eventCount')}
                    helperText={formikError(formik, index, 'eventCount')}
                    inputType="number"
                    gridCol={3}
                    helpText="Number of events a customer gets when they purchase this package"
                  />
                  <WushInputV2
                    label="Cost"
                    value={formik.values.costMatrix[index].cost}
                    onChange={(e) => formik.setFieldValue(`costMatrix.${index}.cost`, e.target.value)}
                    error={formikError(formik, index, 'cost')}
                    helperText={formikError(formik, index, 'cost')}
                    inputType="money"
                    gridCol={3}
                    helpText="Cost for this package"
                  />
                  <Grid item md={3}>
                    <IconButton
                      onClick={() => arrayHelpers.push({ eventCount: 0, cost: 0 })}
                    >
                      <AddCircleIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => arrayHelpers.remove(index)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                  <Grid item md={3} />
                </>
              ))}
            </>
          )}
        />
        <Grid item md={12}>
          <Box mt={2}>
            <Divider />
          </Box>
        </Grid>
        <Grid item md={12}><Box mb={5} /></Grid>
        {showEvents && (
          <FieldArray
            name="events"
            render={(arrayHelpers) => (
              <Grid item={12}>
                <Box mb={3}>
                  <Typography variant="h5">Events</Typography>
                  <Button variant="contained" onClick={() => { setEvent({ name: 'test' }); setShowEventModal(true); }}>Add Events to Series</Button>
                </Box>

                <Box mb={3}>
                  {formik.values.events.map((temporaryEvents, index) => (
                    <Box border="1px solid lightgrey" borderRadius="5px" p={2}>
                      <Stack direction="row" spacing="2rem">
                        <InlineEvent event={temporaryEvents} />
                        <Box>
                          <IconButton
                            onClick={() => arrayHelpers.remove(index)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </Stack>
                    </Box>
                  ))}
                </Box>
                <WushModal
                  open={showCreateModal}
                  onClose={() => setShowEventModal(false)}
                >
                  <Box>
                    <Formik
                      initialValues={{
                        name: formik.values.name,
                        description: formik.values.description,
                        additionalInformation: formik.values.additionalInformation,
                        capacity: formik.values.capacity,
                        startDatetime: formik.values.startDatetime,
                        endDatetime: formik.values.startDatetime,
                        repeatEnd: formik.values.endDatetime,
                      }}
                      onSubmit={(values) => arrayHelpers.push(values)}
                    >
                      {
                        (temporaryFormik) => (
                          <Box>
                            <EventForm
                              selectedEvent={event}
                              values={temporaryFormik.values}
                              formik={temporaryFormik}
                              handleSubmit={() => {
                                setShowEventModal(false);
                                temporaryFormik.handleSubmit();
                              }}
                              onCancel={() => setShowEventModal(false)}
                              eventInSeries={formik.values.name}
                            />
                          </Box>

                        )
                      }
                    </Formik>
                  </Box>
                </WushModal>
              </Grid>
            )}
          />
        )}
        <Grid item md={12}>
          <WushButton
            disabled={formik.values.events && formik.values.events.length === 0}
            variant="contained"
            onClick={formik.handleSubmit}
            loading={eventSeriesCreating}
          >
            {update ? 'Save' : 'Create Series'}
          </WushButton>
        </Grid>
      </Grid>
    </Box>
  );
};

export default observer(EventSeriesForm);
